import Select, { GroupBase } from 'react-select';
import { useFolderTree } from '../../hooks/useFolderTree';
import type { Folder } from '../../types/api';

interface FolderOption {
	label: string;
	value: string;
	folder: Folder;
}

interface FolderGroup extends GroupBase<FolderOption> {
	label: string;
	options: FolderOption[];
}

interface NestedFolderSelectProps {
	value?: string;
	onChange: (folderId: string | undefined) => void;
	placeholder?: string;
	isClearable?: boolean;
	className?: string;
}

/**
 * Nested folder select component using react-select with grouped options
 */
export function NestedFolderSelect({
	value,
	onChange,
	placeholder = 'Select a folder...',
	isClearable = true,
	className = '',
}: NestedFolderSelectProps) {
	const { data: folderTree, isLoading } = useFolderTree();

	// Convert folder tree to flat list with indentation
	const buildFlatOptions = (folders: Folder[], depth = 0): FolderOption[] => {
		const options: FolderOption[] = [];

		for (const folder of folders || []) {
			// Add the folder itself with depth info
			options.push({
				label: folder.name,
				value: folder.id,
				folder: { ...folder, depth },
			});

			// Recursively add children
			if (folder.children && folder.children.length > 0) {
				options.push(...buildFlatOptions(folder.children, depth + 1));
			}
		}

		return options;
	};

	// Build the options structure - simple flat list
	const allOptions: FolderOption[] = [
		{
			label: '📁 Root (No folder)',
			value: '',
			folder: {
				id: '',
				name: 'Root',
				flowCount: 0,
				children: [],
				hasChildren: false,
				isRoot: true,
				depth: 0,
				fullPath: 'Root',
				isEmpty: true,
				sortOrder: 0,
				createdAt: '',
				updatedAt: '',
			} as Folder,
		},
		...buildFlatOptions(folderTree || []),
	];

	// Find the selected option
	const selectedOption = allOptions.find(option => option.value === value);

	// Custom styles for dark mode
	const customStyles = {
		control: (provided: any, state: any) => ({
			...provided,
			backgroundColor: '#374151',
			borderColor: state.isFocused ? '#3B82F6' : '#4B5563',
			color: '#FFFFFF',
			minHeight: '38px',
			'&:hover': {
				borderColor: '#6B7280',
			},
		}),
		menu: (provided: any) => ({
			...provided,
			backgroundColor: '#374151',
			border: '1px solid #4B5563',
			zIndex: 9999,
		}),
		option: (provided: any, state: any) => ({
			...provided,
			backgroundColor: state.isSelected
				? '#2563EB'
				: state.isFocused
				? '#4B5563'
				: 'transparent',
			color: '#FFFFFF',
			'&:hover': {
				backgroundColor: '#4B5563',
			},
		}),
		groupHeading: (provided: any) => ({
			...provided,
			backgroundColor: '#1F2937',
			color: '#D1D5DB',
			fontWeight: 'bold',
			fontSize: '12px',
			padding: '8px 12px',
		}),
		singleValue: (provided: any) => ({
			...provided,
			color: '#FFFFFF',
		}),
		input: (provided: any) => ({
			...provided,
			color: '#FFFFFF',
		}),
		placeholder: (provided: any) => ({
			...provided,
			color: '#9CA3AF',
		}),
	};

	return (
		<div className={className}>
			<Select<FolderOption, false>
				options={allOptions}
				value={selectedOption}
				onChange={option => onChange(option?.value || undefined)}
				placeholder={placeholder}
				isClearable={isClearable}
				isLoading={isLoading}
				isSearchable={true}
				styles={customStyles}
				classNamePrefix="folder-select"
				formatGroupLabel={group => (
					<div className="font-semibold text-gray-300 dark:text-gray-400">
						{group.label}
					</div>
				)}
				formatOptionLabel={option => (
					<div
						className="flex gap-2 items-center"
						style={{ paddingLeft: `${(option.folder?.depth || 0) * 16}px` }}
					>
						{option.folder?.depth && option.folder.depth > 0 && (
							<span className="text-gray-400">└─</span>
						)}
						<span>📁 {option.label}</span>
						{option.folder?.flowCount && option.folder.flowCount > 0 && (
							<span className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
								{option.folder.flowCount} workflows
							</span>
						)}
					</div>
				)}
				noOptionsMessage={() => 'No folders found'}
			/>
		</div>
	);
}

export default NestedFolderSelect;
